import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import type { Poll } from './usePolls';

export interface PollVote {
  pubkey: string;
  responseIds: string[];
  created_at: number;
}

export interface PollResults {
  votes: PollVote[];
  totalVotes: number;
  optionCounts: Record<string, number>;
  userVote?: PollVote;
}

function extractVotes(events: NostrEvent[], poll: Poll, userPubkey?: string): PollResults {
  // Group votes by pubkey, keeping only the most recent valid vote per user
  const voteMap = new Map<string, NostrEvent>();

  events.forEach((event) => {
    // Only count votes within the poll timeframe
    if (poll.endsAt && event.created_at > poll.endsAt) return;

    const existing = voteMap.get(event.pubkey);
    if (!existing || event.created_at > existing.created_at) {
      voteMap.set(event.pubkey, event);
    }
  });

  // Extract response IDs from each vote
  const votes: PollVote[] = Array.from(voteMap.values()).map((event) => {
    const responseIds = event.tags
      .filter(([name]) => name === 'response')
      .map(([_, id]) => id)
      .filter(Boolean);

    // For single choice, only keep the first response
    const finalResponses = poll.pollType === 'singlechoice' 
      ? responseIds.slice(0, 1)
      : Array.from(new Set(responseIds)); // Remove duplicates for multichoice

    return {
      pubkey: event.pubkey,
      responseIds: finalResponses,
      created_at: event.created_at,
    };
  });

  // Count votes per option
  const optionCounts: Record<string, number> = {};
  poll.options.forEach(opt => {
    optionCounts[opt.id] = 0;
  });

  votes.forEach((vote) => {
    vote.responseIds.forEach((responseId) => {
      if (optionCounts[responseId] !== undefined) {
        optionCounts[responseId]++;
      }
    });
  });

  const userVote = userPubkey ? votes.find(v => v.pubkey === userPubkey) : undefined;

  return {
    votes,
    totalVotes: votes.length,
    optionCounts,
    userVote,
  };
}

export function usePollResults(poll: Poll | null, userPubkey?: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['poll-results', poll?.id, userPubkey],
    queryFn: async (c) => {
      if (!poll) return null;

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);
      
      const filter: Record<string, unknown> = {
        kinds: [1018],
        '#e': [poll.id],
      };

      if (poll.endsAt) {
        filter.until = poll.endsAt;
      }

      const relays = poll.relays.length > 0 ? poll.relays : undefined;
      const nostrClient = relays ? nostr.group(relays) : nostr;

      const events = await nostrClient.query([filter], { signal });
      
      return extractVotes(events, poll, userPubkey);
    },
    enabled: !!poll,
    refetchInterval: 5000, // Refetch every 5 seconds for real-time updates
  });
}
